import { keycloak } from 'lib/keycloak'

/**
 * Imperative facade over the `lib/keycloak` singleton — the single seam for token / login /
 * logout outside the React tree. The React layer (`contexts/keycloak` provider, `AuthGate`)
 * keeps using `lib/keycloak` directly for init; everything else (the Apollo link, MobX stores,
 * plain services, class components) goes through `authService`, so swapping the auth
 * implementation again only touches this file.
 */
class AuthService {
  /**
   * Returns a valid access token, refreshing it when it is within 30s of expiry. Centralizes the
   * `authenticated` check + `updateToken(30)` that `link.ts`/`App` ran inline and that
   * `componentsService`/`use-pre-prompts` skipped (the stale-token bug — they read an unrefreshed
   * `keycloak.token`).
   */
  async getAccessToken(): Promise<string | undefined> {
    if (keycloak.authenticated) {
      await keycloak.updateToken(30)
    }

    return keycloak.token
  }

  /**
   * Redirects to the Keycloak login page, returning to `redirectUrl` (or the current location)
   * with `login=true` appended. Ported verbatim from winnin-auth's `customLogin`.
   */
  login(redirectUrl?: string): void {
    const pathname =
      redirectUrl || (window.location.pathname ? `${window.location.pathname}${window.location.search}` : '/')
    const redirectUri = new URL(`${window?.location?.origin}${pathname}`)
    redirectUri.searchParams.set('login', 'true')

    keycloak.login({ redirectUri: redirectUri.href })
  }

  /**
   * Logs out via Keycloak, returning to the current URL. Relies on the native v26
   * `createLogoutUrl` (which auto-appends `id_token_hint`, preserving the silent logout), replacing
   * the dropped `endpoints.logout()` override. `redirectUri` is required — without it the adapter
   * falls back to `this.redirectUri || 'http://localhost'`.
   */
  async logout(): Promise<void> {
    await keycloak.logout({ redirectUri: window.location.href })
  }

  /** Subject claim (`sub`) of the current access token, or `''` when unauthenticated. */
  get userId(): string {
    return keycloak.tokenParsed?.sub || ''
  }

  /** Email claim of the current access token, or `''` when unauthenticated. */
  get userEmail(): string {
    return keycloak.tokenParsed?.email || ''
  }

  /**
   * Builds an absolute URL into the Keycloak account console for the current realm, e.g.
   * `accountUrl('account-security/signing-in')`. Normalizes the server-URL boundary (the
   * configured `ENDPOINT` ends in `/`, but keycloak-js stores it verbatim) so the result is
   * well-formed regardless, and centralizes the realm-path shape that callers used to inline.
   */
  accountUrl(path: string): string {
    const base = (keycloak.authServerUrl ?? '').replace(/\/$/, '')
    const realm = encodeURIComponent(keycloak.realm ?? '')

    return `${base}/realms/${realm}/account/${path}`
  }
}

export const authService = new AuthService()
